import { supabase } from "@/integrations/supabase/client";
import { Recipe, CreateRecipeInput } from "@/types/library";

export const recipesService = {
  // קבלת כל התגיות
  async getAllTags(): Promise<string[]> {
    const { data, error } = await supabase
      .from("recipe_tags")
      .select("name")
      .order("name");

    if (error) throw error;
    return data?.map(tag => tag.name) || [];
  },

  // קבלת כל המתכונים המפורסמים
  async getAllPublishedRecipes(): Promise<Recipe[]> {
    const { data, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("is_published", true)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // קבלת מתכון לפי ID
  async getRecipeById(id: string): Promise<Recipe | null> {
    const { data, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // יצירת מתכון חדש (admin only)
  async createRecipe(input: CreateRecipeInput): Promise<Recipe> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const { data, error } = await supabase
      .from("recipes")
      .insert({
        ...input,
        created_by: user.id,
        is_published: true,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // עדכון מתכון (admin only)
  async updateRecipe(id: string, updates: Partial<Recipe>): Promise<Recipe> {
    const { data, error } = await supabase
      .from("recipes")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // מחיקת מתכון (admin only)
  async deleteRecipe(id: string): Promise<void> {
    const { error } = await supabase.from("recipes").delete().eq("id", id);

    if (error) throw error;
  },

  // חיפוש מתכונים
  async searchRecipes(query: string, tags?: string[]): Promise<Recipe[]> {
    let queryBuilder = supabase
      .from("recipes")
      .select("*")
      .eq("is_published", true);

    if (query) {
      queryBuilder = queryBuilder.or(
        `title.ilike.%${query}%,description.ilike.%${query}%,category.ilike.%${query}%`
      );
    }

    if (tags && tags.length > 0) {
      queryBuilder = queryBuilder.contains("tags", tags);
    }

    const { data, error } = await queryBuilder.order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // סינון מתכונים לפי קטגוריה
  async getRecipesByCategory(category: string): Promise<Recipe[]> {
    const { data, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("is_published", true)
      .eq("category", category)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // סינון מתכונים לפי תגיות
  async getRecipesByTags(tags: string[]): Promise<Recipe[]> {
    const { data, error } = await supabase
      .from("recipes")
      .select("*")
      .eq("is_published", true)
      .contains("tags", tags)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // יצירת תגית חדשה (admin only)
  async createTag(name: string): Promise<void> {
    const { error } = await supabase
      .from("recipe_tags")
      .insert({ name: name.trim() });

    if (error) throw error;
  },

  // עדכון תגית (admin only)
  async updateTag(id: string, name: string): Promise<void> {
    const { error } = await supabase
      .from("recipe_tags")
      .update({ name: name.trim() })
      .eq("id", id);

    if (error) throw error;
  },

  // מחיקת תגית (admin only)
  async deleteTag(id: string): Promise<void> {
    const { error } = await supabase
      .from("recipe_tags")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },
};
